/**
 * OrdersFilterSheet — the mobile Orders filter bottom sheet. Accordion of
 * categories (Status · Payment · Channel · Location) with multi-select chips.
 * Status/Payment/Location filter real data; Channel is "Not supported".
 */

import { useState } from "react";
import { ModalSheet } from "@a2z/ui";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useGovernoratesQuery } from "@/modules/settings/services/useGovernoratesQuery";
import {
  DATE_PRESET_LABEL,
  SHIPMENT_STATUS_LABEL,
  SHIPMENT_STATUS_ORDER,
  STATUS_GROUP_LABEL,
  STATUS_GROUP_ORDER,
  type DatePreset,
  type OrdersFilterState,
  type PaymentKind,
  type StatusGroup,
} from "./orders-mobile-model";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  storeSlug: string;
  value: OrdersFilterState;
  onChange: (next: OrdersFilterState) => void;
};

type Cat = "date" | "status" | "payment" | "shipment" | "channel" | "location";

const DATE_PRESETS: DatePreset[] = ["all", "today", "week", "month"];

export const EMPTY_ORDERS_FILTERS: OrdersFilterState = {
  status: [],
  payment: [],
  governorate: [],
  date: "all",
  shipment: [],
};

const PAYMENTS: { id: PaymentKind; labelKey: string }[] = [
  { id: "deposit", labelKey: keys.orders.payDeposit },
  { id: "cod", labelKey: keys.orders.paymentMethodCod },
  { id: "paid", labelKey: keys.orders.payPaidOnline },
];

export function OrdersFilterSheet({ isOpen, onClose, storeSlug, value, onChange }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const [open, setOpen] = useState<Cat | null>("status");
  const { data: governorates } = useGovernoratesQuery({ storeSlug });
  const govs = (governorates ?? []).filter((g) => g.isActive);

  const total =
    value.status.length +
    value.payment.length +
    value.governorate.length +
    value.shipment.length +
    (value.date !== "all" ? 1 : 0);

  const toggle = <T extends string>(
    key: "status" | "payment" | "governorate" | "shipment",
    v: T,
  ) => {
    const arr = value[key] as string[];
    const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
    onChange({ ...value, [key]: next });
  };
  const setDate = (date: DatePreset) => onChange({ ...value, date });

  const govName = (id: string) => {
    const g = govs.find((x) => x.id === id);
    if (!g) return id;
    return locale === "ar" ? g.nameAr : g.nameEn ?? g.nameAr;
  };

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="sm" surface="content">
      <div className="flex max-h-[80vh] flex-col">
        <div className="flex items-center gap-2.5 px-4 pb-2 pt-4">
          <span className="text-[color:var(--text-muted)]">
            <FilterIcon />
          </span>
          <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.orders.filterTitle)}
          </h2>
          <span className="flex-1" />
          {total > 0 ? (
            <button
              type="button"
              onClick={() => onChange(EMPTY_ORDERS_FILTERS)}
              className="text-[12.5px] font-semibold text-[color:var(--text-secondary)]"
            >
              {t(keys.orders.clearAll)}
            </button>
          ) : null}
        </div>

        <div className="overflow-y-auto px-2 pb-4">
          <Category
            label={t(keys.orders.filterDateCat)}
            count={value.date !== "all" ? 1 : 0}
            open={open === "date"}
            onToggle={() => setOpen(open === "date" ? null : "date")}
          >
            {DATE_PRESETS.map((p) => (
              <Chip
                key={p}
                label={t(DATE_PRESET_LABEL[p])}
                on={value.date === p}
                onClick={() => setDate(p)}
              />
            ))}
          </Category>

          <Category
            label={t(keys.orders.filterStatusCat)}
            count={value.status.length}
            open={open === "status"}
            onToggle={() => setOpen(open === "status" ? null : "status")}
          >
            {STATUS_GROUP_ORDER.map((sg: StatusGroup) => (
              <Chip
                key={sg}
                label={t(STATUS_GROUP_LABEL[sg])}
                on={value.status.includes(sg)}
                onClick={() => toggle("status", sg)}
              />
            ))}
          </Category>

          <Category
            label={t(keys.orders.filterPaymentCat)}
            count={value.payment.length}
            open={open === "payment"}
            onToggle={() => setOpen(open === "payment" ? null : "payment")}
          >
            {PAYMENTS.map((p) => (
              <Chip
                key={p.id}
                label={t(p.labelKey)}
                on={value.payment.includes(p.id)}
                onClick={() => toggle("payment", p.id)}
              />
            ))}
          </Category>

          <Category
            label={t(keys.orders.filterShipmentCat)}
            count={value.shipment.length}
            open={open === "shipment"}
            onToggle={() => setOpen(open === "shipment" ? null : "shipment")}
          >
            {SHIPMENT_STATUS_ORDER.map((s) => (
              <Chip
                key={s}
                label={t(SHIPMENT_STATUS_LABEL[s])}
                on={value.shipment.includes(s)}
                onClick={() => toggle("shipment", s)}
              />
            ))}
          </Category>

          <Category
            label={t(keys.orders.filterChannelCat)}
            count={0}
            open={open === "channel"}
            onToggle={() => setOpen(open === "channel" ? null : "channel")}
            ns
          >
            <span className="px-1 py-2 text-[12.5px] text-[color:var(--text-muted)]">
              {t(keys.orders.notSupported)}
            </span>
          </Category>

          <Category
            label={t(keys.orders.filterLocationCat)}
            count={value.governorate.length}
            open={open === "location"}
            onToggle={() => setOpen(open === "location" ? null : "location")}
          >
            {govs.map((g) => (
              <Chip
                key={g.id}
                label={govName(g.id)}
                on={value.governorate.includes(g.id)}
                onClick={() => toggle("governorate", g.id)}
              />
            ))}
          </Category>
        </div>
      </div>
    </ModalSheet>
  );
}

function Category({
  label,
  count,
  open,
  onToggle,
  ns = false,
  children,
}: {
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  ns?: boolean;
  children: React.ReactNode;
}) {
  const t = useTranslations();
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2.5 rounded-[10px] px-2 py-3 text-start transition-colors hover:bg-[color:var(--soft)]"
      >
        <span className="text-[14.5px] font-semibold text-[color:var(--text-primary)]">{label}</span>
        <span className="flex-1" />
        {ns ? (
          <span className="rounded-full border border-[color:var(--line)] px-1.5 py-0.5 text-[9px] font-semibold uppercase text-[color:var(--text-muted)]">
            {t(keys.orders.notSupported)}
          </span>
        ) : count > 0 ? (
          <span
            className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10.5px] font-bold"
            style={{ background: "var(--accent-500)", color: "var(--on-accent)" }}
          >
            {count}
          </span>
        ) : null}
        <ChevronIcon rotated={open} />
      </button>
      {open ? <div className="flex flex-wrap gap-2 px-2 pb-3 pt-1">{children}</div> : null}
    </div>
  );
}

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-3 py-2 text-[13px] font-semibold transition-colors"
      style={{
        background: on ? "color-mix(in srgb, var(--accent-500) 14%, transparent)" : "transparent",
        border: `1px solid ${on ? "var(--accent-500)" : "var(--line)"}`,
        color: on ? "var(--accent-500)" : "var(--text-muted)",
      }}
    >
      {label}
    </button>
  );
}

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function FilterIcon() {
  return (
    <svg {...ICON} width={17} height={17} strokeWidth={1.9}>
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}
function ChevronIcon({ rotated }: { rotated: boolean }) {
  return (
    <svg
      {...ICON}
      width={14}
      height={14}
      strokeWidth={2.4}
      className="text-[color:var(--text-muted)] transition-transform"
      style={{ transform: rotated ? "rotate(90deg)" : "rotate(0deg)" }}
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
